import React from 'react';
import { useTheme } from '@mui/material/styles';
import { Box, Container, Typography } from '@mui/material';
import { useI18nContext } from '@procore/core-react';
import { useRouter } from 'next/router';
import OldHero from '../Heros/OldHero';
import { getStateList, type StateType } from '../../data/location/states';
import NoStyleLink from '../ButtonsLinks/NoStyleLink';
import ServiceAreaIcon from '../CoverageAreas/ServiceAreaIcon';
import { FilterUrlKey } from '../../helpers/filters';
import { useStateCardStyles, useStyles } from './StatePageContent.styles';
import {
  type CountryType,
  DEFAULT_COUNTRY,
  CANADA,
} from '../../data/location/countries';
import AnalyticsWrappedComponent from '../Analytics/AnalyticsWrappedComponent';
import { type SeoListType } from '../../types/seo-list-type';

type HeroProps = {
  title: string;
  subTitle: string;
};

type GetStateLinkType = (
  item: SeoListType,
  country: CountryType,
  state: StateType
) => string;

interface StatePageContentProps {
  hero: HeroProps;
  item: SeoListType;
  itemType?: FilterUrlKey;
  getStateLink: GetStateLinkType;
}

interface StateCardProps {
  item: SeoListType;
  getStateLink: GetStateLinkType;
  country: CountryType;
  state: StateType;
}

export const StateCard = ({
  item,
  country,
  state,
  getStateLink,
}: StateCardProps) => {
  const classes = useStateCardStyles();
  const label = `${item.name} in ${state.abbreviation}`;

  return (
    <Box>
      <AnalyticsWrappedComponent
        componentType={NoStyleLink}
        eventText="Clicked Browse By Location Link"
        eventLabel={label}
        data-track-click={`Illustration Card, Navigation, ${label}`}
        href={getStateLink(item, country, state)}
      >
        <Box
          className={classes.stateCard}
          display="flex"
          justifyContent="center"
          alignItems="center"
          py={2}
          px={5}
        >
          <Box textAlign="center" className={classes.stateIcon}>
            <ServiceAreaIcon
              countryCode={country.alpha2}
              stateCode={state.abbreviation}
            />
          </Box>
        </Box>
        <Box mt={2} width="100%" textAlign="center">
          <Typography className={classes.title} component="h3" variant="h3">
            {label}
          </Typography>
        </Box>
      </AnalyticsWrappedComponent>
    </Box>
  );
};

const StatePageContent = ({
  item,
  itemType,
  hero,
  getStateLink,
}: StatePageContentProps) => {
  const { locale } = useRouter();
  const I18n = useI18nContext();
  const classes = useStyles();
  const theme = useTheme();

  const country =
    locale === 'en-CA' || locale === 'fr-CA' ? CANADA : DEFAULT_COUNTRY;

  return (
    <>
      <OldHero
        title={hero.title}
        subTitle={hero.subTitle}
        searchBarOverrideItem={item}
        filter={itemType}
      />
      <Container>
        <Box mt={7}>
          <Typography component="h2" variant="h2">
            {I18n.t('components.state_page_content.browse_by_location')}
          </Typography>
        </Box>
        <Box
          className={classes.stateGrid}
          mt={7}
          component="section"
          display="grid"
          gap={`${theme.spacing(5)} ${theme.spacing(3)}`}
          gridTemplateColumns="repeat(auto-fit, minmax(17%, 1fr))"
          id="browse-by-location-section"
        >
          {getStateList(country).map((state) => {
            return (
              <StateCard
                key={state.abbreviation}
                country={country}
                state={state}
                item={item}
                getStateLink={getStateLink}
              />
            );
          })}
        </Box>
        <Box mt={10}>
          {item.description && (
            <>
              <Typography component="h2" variant="h2">
                {item.name}
              </Typography>
              <Box mt={3} mb={10}>
                <Typography>{item.description}</Typography>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </>
  );
};

export default StatePageContent;
